// /shadow/post/:id — Single-analysis SPA page. Mounted as a thin wrapper around
// the existing <AnalysisCard /> so we never diverge from the feed look-and-feel.
// The crawler-friendly OG variant lives at /api/og/post/:id (backend HTML).
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AnalysisCard } from "../components/AnalysisCard";
import { API, safeJson } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

export default function ShadowPostPage() {
  const { id } = useParams();
  const { lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tok = localStorage.getItem("auth_token") || localStorage.getItem("rebel_jwt");
        const r = await fetch(`${API}/shadow-posts/${id}`, {
          credentials: "include",
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        const j = await safeJson(r);
        if (!mounted) return;
        if (!r.ok) {
          setError(j?.detail || "not_found");
        } else {
          setPost(j?.analysis || j);
        }
      } catch (e) {
        if (mounted) setError(e.message || "load_error");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const t = (k) => ({
    es: { back: "// SHADOW NETWORK", loading: "Cargando análisis…", missing: "Este análisis no existe o fue eliminado.", browse: "Volver al feed" },
    en: { back: "// SHADOW NETWORK", loading: "Loading analysis…", missing: "This analysis does not exist or was removed.", browse: "Back to feed" },
    sk: { back: "// SHADOW NETWORK", loading: "Načítavam analýzu…", missing: "Analýza neexistuje alebo bola odstránená.", browse: "Späť na feed" }, de: { back: "// SHADOW NETWORK", loading: "Analyse wird geladen…", missing: "Diese Analyse existiert nicht oder wurde gelöscht.", browse: "Zurück zum Feed" }, "zh-CN": { back: "// SHADOW NETWORK", loading: "加载分析中…", missing: "此分析不存在或已被删除。", browse: "返回动态" },
  }[lang] || {})[k];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="shadow-post-page">
      <Navbar />
      <section className="mx-auto max-w-2xl px-6 pt-28 pb-24">
        <Link
          to="/shadow"
          data-testid="shadow-post-back"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/45 hover:text-amber-300"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("back")}
        </Link>

        {loading ? (
          <div className="mt-10 flex items-center justify-center gap-2 text-white/55">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("loading")}
          </div>
        ) : error || !post ? (
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.025] p-8 text-center" data-testid="shadow-post-missing">
            <AlertCircle className="mx-auto h-8 w-8 text-amber-300/70" />
            <p className="mt-3 text-sm text-white/55">{t("missing")}</p>
            <Link to="/shadow" className="mt-4 inline-block text-xs font-mono uppercase tracking-wider text-amber-300 hover:text-amber-200">
              {t("browse")} →
            </Link>
          </div>
        ) : (
          <div className="mt-6" data-testid="shadow-post-card">
            <AnalysisCard analysis={post} />
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
